"use professional"
import { useState, useEffect } from "react"
import Select from 'react-select'
import { useAuth } from "../../AuthContext"
import { useNavigate } from "react-router-dom"
import { useLanguage } from "../../contexts/LanguageContext";

function AddSkillsForm() {
  const [skillCount, setSkillCount] = useState(0)
  const [skills, setSkills] = useState([])
  const [availableSkills, setAvailableSkills] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [technologyOptions, setTechnologyOptions] = useState([])
  const [customSkillInputs, setCustomSkillInputs] = useState({})
  const { user } = useAuth()
  const { t } = useLanguage();
  const navigate = useNavigate()
  const professionalId = user?.secondId

  useEffect(() => {
    fetch("http://localhost:5000/skills")
      .then(response => response.json())
      .then(data => {
        const formattedOptions = data.map(skill => ({
          value: skill.skill_id,
          label: skill.skill_name.charAt(0).toUpperCase() + skill.skill_name.slice(1)
        }))
        // Move "Other" to the first position
        formattedOptions.unshift({ value: "__other__", label: t("other") })
        setTechnologyOptions(formattedOptions)
      })
      .catch(error => console.error("Error fetching skills:", error))
  }, [])

  const handleSkillCountChange = (e) => {
    const count = Number.parseInt(e.target.value)
    setSkillCount(count)
    setSkills(Array(count).fill({ skillid: "", experience: "" }))
  }

  const handleSkillChange = (index, field, value) => {
    const updatedSkills = [...skills];
    updatedSkills[index] = { ...updatedSkills[index], [field]: value };
    setSkills(updatedSkills);
  };

  const handleCustomSkillInput = (index, value) => {
    setCustomSkillInputs({ ...customSkillInputs, [index]: value })
    // Set a placeholder object for custom skill in skills array
    const updatedSkills = [...skills]
    updatedSkills[index] = { ...updatedSkills[index], customSkill: value }
    setSkills(updatedSkills)
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Prepare arrays for existing and custom skills
    const existingSkills = []
    const customSkills = []
    skills.forEach((skill, idx) => {
      if (skill.skillid && skill.skillid.value === "__other__") {
        if (customSkillInputs[idx] && customSkillInputs[idx].trim()) {
          customSkills.push({
            skill_name: customSkillInputs[idx].trim(),
            experience: skill.experience
          })
        }
      } else if (skill.skillid && skill.skillid.value) {
        existingSkills.push({
          skillid: skill.skillid.value,
          experience: skill.experience
        })
      }
    })
    try {
      const response = await fetch("http://localhost:5000/proskills", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ skills: existingSkills, customSkills, professionalId }),
      });
      const result = await response.json();
      if (response.ok) {
        alert(t("skillsSubmittedSuccess"));
        navigate("/ProProject")
      } else {
        alert(t("skillsSubmitFailed"));
      }
    } catch (error) {
      console.log("Error submitting skills:", error);
      alert(t("skillsSubmitError"));
    }
  };

  const filteredSkills = availableSkills.filter(skill =>
    skill.skill_name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const inputStyle = {
    background: 'var(--input-bg)', color: 'var(--text-primary)', border: '1px solid var(--input-border)',
    borderRadius: '10px', padding: '10px 14px', width: '100%', outline: 'none', fontSize: '0.9rem',
    transition: 'border-color 0.2s, background 0.2s',
  };
  const labelStyle = { display: 'block', color: 'var(--text-secondary)', fontSize: '0.8rem', fontWeight: 600, marginBottom: '6px' };

  const selectStyles = {
    control: (base) => ({ ...base, background: 'var(--input-bg)', borderColor: 'var(--input-border)', borderRadius: '10px', minHeight: '42px', boxShadow: 'none', '&:hover': { borderColor: 'rgba(var(--accent-primary-rgb), 0.5)' } }),
    menu: (base) => ({ ...base, background: 'var(--bg-card-solid)', border: '1px solid var(--border-glass)', borderRadius: '10px' }),
    option: (base, state) => ({ ...base, background: state.isFocused ? 'var(--bg-elevated)' : 'transparent', color: 'var(--text-primary)', cursor: 'pointer' }),
    singleValue: (base) => ({ ...base, color: 'var(--text-primary)' }),
    input: (base) => ({ ...base, color: 'var(--text-primary)' }),
    placeholder: (base) => ({ ...base, color: 'var(--text-muted)' }),
  };

  return (
    <div style={{ maxWidth: '720px', margin: '40px auto', background: 'var(--bg-card)', borderRadius: '20px', border: '1px solid var(--border-glass)', padding: '40px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, #10b981, var(--accent-cyan))' }} />
      <h1 style={{ fontSize: '1.6rem', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-display)', marginBottom: '6px' }}>{t('addYourSkills')}</h1>
      <p style={{ color: 'var(--text-muted)', marginBottom: '24px', fontSize: '0.9rem' }}>{t('addYourSkillsSubtitle')}</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="numberOfSkills" style={labelStyle}>{t('numberOfSkills')}</label>
          <input type="number" id="numberOfSkills" style={inputStyle} placeholder={t('numberOfSkillsPlaceholder')} min="0" required value={skillCount} onChange={handleSkillCountChange}
            onFocus={e => { e.target.style.borderColor = 'rgba(var(--accent-primary-rgb), 0.6)'; e.target.style.background = 'var(--input-focus-bg)'; }} onBlur={e => { e.target.style.borderColor = 'var(--input-border)'; e.target.style.background = 'var(--input-bg)'; }} />
        </div>

        {skills.map((skill, index) => (
          <div key={index} style={{ padding: '20px', border: '1px solid var(--border-glass)', borderRadius: '14px', background: 'var(--bg-glass)', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '14px' }}>{t('skill')} {index + 1}</h3>
            <div className="mb-4">
              <label style={labelStyle} htmlFor={`skillName${index}`}>{t('skillName')}</label>
              <div>
                <Select options={technologyOptions.filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase()))}
                  value={skill.skillid} onChange={(selectedOption) => handleSkillChange(index, 'skillid', selectedOption)}
                  styles={selectStyles} className="w-full" />
                {skill.skillid && skill.skillid.value === "__other__" && (
                  <input type="text" placeholder={t('enterCustomSkill')} value={customSkillInputs[index] || ""}
                    onChange={e => handleCustomSkillInput(index, e.target.value)}
                    style={{ ...inputStyle, marginTop: '8px' }}
                    onFocus={e => { e.target.style.borderColor = 'rgba(var(--accent-primary-rgb), 0.6)'; e.target.style.background = 'var(--input-focus-bg)'; }} onBlur={e => { e.target.style.borderColor = 'var(--input-border)'; e.target.style.background = 'var(--input-bg)'; }} />
                )}
              </div>
            </div>
            <div className="mb-4">
              <label style={labelStyle} htmlFor={`yearsOfExperience${index}`}>{t('yearsOfExperience')}</label>
              <input style={inputStyle} id={`yearsOfExperience${index}`} type="number" placeholder={t('yearsOfExperiencePlaceholder')} min="0" required value={skill.experience}
                onChange={(e) => handleSkillChange(index, "experience", e.target.value)}
                onFocus={e => { e.target.style.borderColor = 'rgba(var(--accent-primary-rgb), 0.6)'; e.target.style.background = 'var(--input-focus-bg)'; }} onBlur={e => { e.target.style.borderColor = 'var(--input-border)'; e.target.style.background = 'var(--input-bg)'; }} />
            </div>
          </div>
        ))}

        <button type="submit"
          style={{ width: '100%', background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', border: 'none', padding: '12px 20px', borderRadius: '10px', fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem', transition: 'opacity 0.2s' }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.85'} onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
          {t('addSkills')}
        </button>
      </form>
    </div>
  )
}

export default AddSkillsForm

